import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { getTopPurchases } from '../../utils/purchaseAnalytics';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { formatDate } from '../../utils/dateUtils';
import ProgressDashes from '../../components/ProgressDashes';
import { StoryNavigation } from '../../components/StoryNavigation';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';

const TopPurchasesPage = () => {
  const navigate = useNavigate();
  const transactions = useSelector((state: RootState) => state.transactions.items);
  const topPurchases = useMemo(() => getTopPurchases(transactions), [transactions]);
  const { goToNext, goToPrev, hasNext, hasPrev } = useStoryNavigation();

  // Auto-navigate to the next page after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/insights/monthly-spending');
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-[#221A51]/[0.07] relative">
      {/* Progress dashes */}
      <ProgressDashes currentStep={1} totalSteps={11} />
      <StoryNavigation
        onNext={goToNext}
        onPrev={goToPrev}
        hasNext={hasNext}
        hasPrev={hasPrev}
      />

      <div className="px-4 sm:px-16 pt-16 sm:pt-20 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-1 sm:space-y-2"
        >
          <h1 className="text-2xl sm:text-[34px] leading-[140%] font-black text-[#221A51]">
            Your top
            <br />
            purchases
          </h1>
          <p className="text-sm sm:text-base text-[#221A51]/60 font-medium">
            Here are your biggest expenses of the year.
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 sm:mt-12 relative"
        >
          {/* Shadow box */}
          <div className="absolute top-[4px] left-[4px] w-full h-full bg-black rounded-xl" />
          
          {/* Main content box */}
          <div className="bg-white rounded-xl p-4 sm:p-6 relative border-2 border-black">
            {topPurchases.map((purchase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className={`flex justify-between items-start sm:items-center py-3 sm:py-4 ${
                  index !== topPurchases.length - 1 ? 'border-b border-gray-200' : ''
                }`}
              >
                <div>
                  <div className="font-extrabold text-xl sm:text-[28px] leading-tight text-[#221A51] break-words max-w-[180px] sm:max-w-none">
                    {purchase.merchant}
                  </div>
                  <div className="text-xs sm:text-sm text-[#221A51]/60 mt-0.5 sm:mt-1">
                    {formatDate(purchase.date)}
                  </div>
                </div>
                <div className="text-base sm:text-[18px] font-semibold text-[#221A51] text-right ml-4">
                  {formatCurrency(purchase.amount)}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Decorative gear */}
      <motion.div
        initial={{ rotate: -30, scale: 0.8, opacity: 0 }}
        animate={{ rotate: 0, scale: 1, opacity: 1 }}
        transition={{ delay: 0.8, type: "spring" }}
        className="absolute -bottom-20 -right-20 w-64 h-64"
      >
        <div className="w-full h-full rounded-full bg-[#9B86EA]/20 relative overflow-hidden"
          style={{
            maskImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M100 0 L100 40 L160 40 L160 0 Z' fill='%23000'/%3E%3Cpath d='M100 160 L100 200 L160 200 L160 160 Z' fill='%23000'/%3E%3Cpath d='M0 100 L40 100 L40 160 L0 160 Z' fill='%23000'/%3E%3Cpath d='M160 100 L200 100 L200 160 L160 160 Z' fill='%23000'/%3E%3C/svg%3E")`,
            WebkitMaskImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M100 0 L100 40 L160 40 L160 0 Z' fill='%23000'/%3E%3Cpath d='M100 160 L100 200 L160 200 L160 160 Z' fill='%23000'/%3E%3Cpath d='M0 100 L40 100 L40 160 L0 160 Z' fill='%23000'/%3E%3Cpath d='M160 100 L200 100 L200 160 L160 160 Z' fill='%23000'/%3E%3C/svg%3E")`
          }}
        >
          <div className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, rgba(155, 134, 234, 0.2) 0%, rgba(155, 134, 234, 0.4) 100%)`,
              filter: 'blur(4px)'
            }}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default TopPurchasesPage;
